import React from 'react';
import { AlertTriangle, Info, CheckCircle2 } from 'lucide-react';

interface DocumentRendererProps {
  content: string;
  variant: 'rfq' | 'technical';
}

// Parse the structured text into renderable sections
export const DocumentRenderer: React.FC<DocumentRendererProps> = ({ content, variant }) => {
  if (!content) return null;

  // Parse sections from the content
  const sections = parseSections(content);

  return (
    <div className="document-renderer space-y-6">
      {sections.map((section, idx) => (
        <RenderSection key={idx} section={section} variant={variant} index={idx} />
      ))}
    </div>
  );
};

interface ParsedSection {
  title: string;
  number?: string;
  content: string;
  type: 'heading' | 'table' | 'notes' | 'normal';
}

function parseSections(content: string): ParsedSection[] {
  const sections: ParsedSection[] = [];
  const lines = content.split('\n');
  let currentSection: ParsedSection | null = null;
  let buffer: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    // Detect section headers (numbered or capitalized)
    const sectionMatch = trimmed.match(/^(\d+)\.\s+(.+)$/);
    const capsHeader = trimmed.match(/^([A-Z][A-Z\s&/]+)$/);
    const hashHeader = trimmed.match(/^#{1,3}\s+(.+)$/);

    if (sectionMatch || capsHeader || hashHeader) {
      // Save previous section
      if (currentSection) {
        currentSection.content = buffer.join('\n').trim();
        determineSectionType(currentSection);
        sections.push(currentSection);
      }

      const title = sectionMatch ? sectionMatch[2] : (hashHeader ? hashHeader[1] : (capsHeader ? capsHeader[1] : trimmed));
      const number = sectionMatch ? sectionMatch[1] : undefined;

      currentSection = {
        title: title.replace(/[═─]/g, '').trim(),
        number,
        content: '',
        type: 'normal',
      };
      buffer = [];
    } else if (trimmed.startsWith('═') || trimmed.startsWith('─') || trimmed === '---SECTION_BREAK---') {
      // Skip separator lines
      continue;
    } else {
      buffer.push(line);
    }
  }

  // Push last section
  if (currentSection) {
    currentSection.content = buffer.join('\n').trim();
    determineSectionType(currentSection);
    sections.push(currentSection);
  }

  // If no sections parsed, render as single block
  if (sections.length === 0 && content.trim()) {
    sections.push({
      title: '',
      content: content.trim(),
      type: 'normal',
    });
  }

  return sections;
}

function determineSectionType(section: ParsedSection) {
  const titleLower = section.title.toLowerCase();
  const content = section.content;

  // Detect table format (pipes or consistent tabular structure)
  if (content.includes('|') && content.split('\n').filter(l => l.includes('|')).length >= 2) {
    section.type = 'table';
  }
  // Detect engineering notes / warnings
  else if (titleLower.includes('engineering note') || titleLower.includes('warning') || titleLower.includes('flag')) {
    section.type = 'notes';
  }
}

const RenderSection: React.FC<{ section: ParsedSection; variant: string; index: number }> = ({ section, variant, index }) => {
  // Skip empty sections
  if (!section.title && !section.content) return null;

  const isNotes = section.type === 'notes' ||
    section.title.toLowerCase().includes('engineering note') ||
    section.title.toLowerCase().includes('note');

  const isAssumptions = section.title.toLowerCase().includes('assumption') ||
    section.title.toLowerCase().includes('clarification');

  return (
    <div className={`${index > 0 ? 'pt-5' : ''}`}>
      {/* Section Header */}
      {section.title && (
        <div className="flex items-baseline gap-3 mb-3">
          {section.number && (
            <span className="text-orange-400 font-mono text-sm font-bold">{section.number}.</span>
          )}
          <h3 className={`font-['Oswald'] text-base uppercase tracking-wide ${
            variant === 'technical' ? 'text-white' : 'text-slate-200'
          }`}>
            {section.title}
          </h3>
        </div>
      )}

      {/* Section Content */}
      <div className={`pl-${section.number ? '6' : '0'}`}>
        {section.type === 'table' ? (
          <RenderTable content={section.content} />
        ) : isNotes ? (
          <RenderNotesBox content={section.content} />
        ) : isAssumptions ? (
          <RenderAssumptions content={section.content} />
        ) : (
          <RenderContent content={section.content} />
        )}
      </div>
    </div>
  );
};

// Render table from pipe-delimited or structured content
const RenderTable: React.FC<{ content: string }> = ({ content }) => {
  const lines = content.split('\n').filter(l => l.trim());
  const tableLines = lines.filter(l => l.includes('|'));
  const nonTableLines = lines.filter(l => !l.includes('|') && !l.trim().match(/^[-─]+$/));

  // Parse table rows
  const rows = tableLines
    .filter(l => !l.trim().match(/^[\|:\s-]+$/)) // skip separator rows
    .map(l => l.split('|').map(cell => cell.trim()).filter(Boolean));

  if (rows.length === 0) {
    return <RenderContent content={content} />;
  }

  const headerRow = rows[0];
  const dataRows = rows.slice(1);

  return (
    <div className="space-y-3">
      {nonTableLines.length > 0 && (
        <p className="text-slate-300 text-sm leading-relaxed">{nonTableLines.join(' ')}</p>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="border-b border-slate-600">
              {headerRow.map((cell, i) => (
                <th key={i} className="text-left py-2 px-3 text-slate-400 font-semibold text-xs uppercase tracking-wider">
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, ri) => (
              <tr key={ri} className="border-b border-slate-700/50 hover:bg-slate-800/30">
                {row.map((cell, ci) => (
                  <td key={ci} className={`py-2.5 px-3 ${ci === 0 ? 'text-white font-medium' : 'text-slate-300'} text-sm`}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Render engineering notes as highlighted box
const RenderNotesBox: React.FC<{ content: string }> = ({ content }) => {
  const lines = content.split('\n').filter(l => l.trim());
  const isWarning = content.toLowerCase().includes('⚠') || content.toLowerCase().includes('warning') || content.toLowerCase().includes('critical');

  return (
    <div className={`rounded-lg border p-4 ${
      isWarning
        ? 'bg-amber-500/5 border-amber-500/30'
        : 'bg-blue-500/5 border-blue-500/30'
    }`}>
      <div className="flex items-start gap-3">
        {isWarning ? (
          <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
        ) : (
          <Info className="w-5 h-5 text-blue-400 mt-0.5 shrink-0" />
        )}
        <div className="space-y-2">
          {lines.map((line, i) => {
            const cleanLine = line.replace(/^[-•▸]\s*/, '').trim();
            if (!cleanLine) return null;
            return (
              <p key={i} className={`text-sm leading-relaxed ${isWarning ? 'text-amber-200' : 'text-blue-200'}`}>
                {cleanLine}
              </p>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Render assumptions as checklist-style
const RenderAssumptions: React.FC<{ content: string }> = ({ content }) => {
  const lines = content.split('\n').filter(l => l.trim());

  return (
    <div className="space-y-2">
      {lines.map((line, i) => {
        const cleanLine = line.replace(/^[-•▸*]\s*/, '').trim();
        if (!cleanLine) return null;
        return (
          <div key={i} className="flex items-start gap-2.5">
            <CheckCircle2 className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
            <span className="text-slate-300 text-sm leading-relaxed">{cleanLine}</span>
          </div>
        );
      })}
    </div>
  );
};

// Render general content with smart formatting
const RenderContent: React.FC<{ content: string }> = ({ content }) => {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let currentGroup: string[] = [];
  let isInSubgroup = false;
  let subgroupTitle = '';

  const flushGroup = () => {
    if (currentGroup.length > 0) {
      elements.push(
        <p key={`p-${elements.length}`} className="text-slate-300 text-sm leading-relaxed">
          {currentGroup.join(' ')}
        </p>
      );
      currentGroup = [];
    }
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const trimmed = line.trim();

    if (!trimmed) {
      flushGroup();
      continue;
    }

    // Sub-heading (bracketed or bold)
    const subHeadMatch = trimmed.match(/^\[(.+)\]$/) || trimmed.match(/^\*\*(.+)\*\*$/);
    if (subHeadMatch) {
      flushGroup();
      elements.push(
        <h4 key={`sh-${i}`} className="text-orange-300/90 text-sm font-semibold mt-4 mb-1.5 uppercase tracking-wide">
          {subHeadMatch[1]}
        </h4>
      );
      continue;
    }

    // Equipment-style line (Component — Specification — Standard)
    const equipMatch = trimmed.match(/^(.+?)\s*[—–-]\s*(.+?)(?:\s*[—–-]\s*(.+))?$/);
    if (equipMatch && !trimmed.startsWith('-') && trimmed.includes('—')) {
      flushGroup();
      elements.push(
        <div key={`eq-${i}`} className="flex items-baseline gap-2 py-1 pl-4 border-l-2 border-slate-700">
          <span className="text-white text-sm font-medium">{equipMatch[1]}</span>
          <span className="text-slate-500 text-sm">—</span>
          <span className="text-slate-400 text-sm">{equipMatch[2]}</span>
          {equipMatch[3] && (
            <>
              <span className="text-slate-500 text-sm">—</span>
              <span className="text-orange-400/70 text-xs font-mono">{equipMatch[3]}</span>
            </>
          )}
        </div>
      );
      continue;
    }

    // Bullet / list item
    if (trimmed.match(/^[-•▸*]\s/)) {
      flushGroup();
      const itemText = trimmed.replace(/^[-•▸*]\s*/, '');
      elements.push(
        <div key={`li-${i}`} className="flex items-start gap-2 pl-2 py-0.5">
          <span className="text-orange-400/60 text-xs mt-1.5">▪</span>
          <span className="text-slate-300 text-sm leading-relaxed">{itemText}</span>
        </div>
      );
      continue;
    }

    // Regular paragraph line
    currentGroup.push(trimmed);
  }

  flushGroup();

  return <div className="space-y-2">{elements}</div>;
};
